export const enum Terrain {
  Grass = 0,
  Dirt = 1,
  Sand = 2,
  Rock = 3,
  Snow = 4,
  Shallow = 5,
  Water = 6,
  Forest = 7,
  Road = 8,
}

export type MapType =
  | 'continental'
  | 'highlands'
  | 'forest'
  | 'desert'
  | 'riverValley'
  | 'coastal'
  | 'mountainPass'
  | 'openPlains'
  | 'islands'
  | 'archipelago';

export type MapSize = 'small' | 'medium' | 'large' | 'huge';

export const MAP_SIZES: Record<MapSize, number> = { small: 96, medium: 128, large: 160, huge: 200 };

/**
 * Most resources (trees, mines, bushes, fish, animals) a map may place. The world holds 8,192
 * entities (MAX_ENTITIES), and the rest are kept for players: towns, armies and walls for up to 8
 * players. A map that generates more thins its outlying trees to fit.
 */
export const RESOURCE_BUDGET = 4800;

export interface MapTypeInfo {
  key: MapType;
  name: string;
  description: string;
  /** Requires naval units to be fully playable. */
  naval: boolean;
}

export const MAP_TYPES: MapTypeInfo[] = [
  { key: 'continental', name: 'Continental', description: 'A large landmass ringed by sea.', naval: false },
  { key: 'highlands', name: 'Highlands', description: 'Rolling hills and cliffs reward holding high ground.', naval: false },
  { key: 'forest', name: 'Forest', description: 'Dense woodland with narrow clearings.', naval: false },
  { key: 'desert', name: 'Desert', description: 'Arid dunes with scarce wood around oases.', naval: false },
  { key: 'riverValley', name: 'River Valley', description: 'A river splits the map, crossable at shallow fords.', naval: false },
  { key: 'coastal', name: 'Coastal', description: 'Land along a long coastline.', naval: false },
  { key: 'mountainPass', name: 'Mountain Pass', description: 'Mountain ranges with defensible passes.', naval: false },
  { key: 'openPlains', name: 'Open Plains', description: 'Flat grassland with little cover.', naval: false },
  { key: 'islands', name: 'Islands', description: 'Each player starts on an island linked by shallow reefs.', naval: false },
  { key: 'archipelago', name: 'Archipelago', description: 'Many small islands joined by shallow sandbars.', naval: false },
];

export interface ResourceSpawn {
  kind: string;
  /** Tile coordinates. */
  tx: number;
  ty: number;
}

export interface GameMap {
  size: number;
  type: MapType;
  seed: number;
  /** Vertex heights, (size+1)^2, in height units (HEIGHT_SCALE per world unit). */
  heights: Int16Array;
  /** Per-tile terrain type. */
  terrain: Uint8Array;
  /** Per-tile static passability for land units: 1 passable. */
  passable: Uint8Array;
  /** Start tile positions per player slot. */
  starts: { tx: number; ty: number }[];
  resources: ResourceSpawn[];
}

export const HEIGHT_SCALE = 64;
